import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Edit, Trash2, Loader2, Repeat, CalendarCheck, Upload } from "lucide-react";

const WEEKDAYS = [
  { i: 0, label: "Lun", full: "Lunedì" },
  { i: 1, label: "Mar", full: "Martedì" },
  { i: 2, label: "Mer", full: "Mercoledì" },
  { i: 3, label: "Gio", full: "Giovedì" },
  { i: 4, label: "Ven", full: "Venerdì" },
  { i: 5, label: "Sab", full: "Sabato" },
  { i: 6, label: "Dom", full: "Domenica" },
];

const EMPTY_TPL = {
  name: "",
  description: "",
  ingredients: "",
  allergens: "",
  portions_total: 10,
  price: 8,
  cutoff_time: "11:30",
  cutoff_offset_days: 0,
  weekdays: [],
  photo_path: null,
  pickup_location: "",
  active: true,
};

export default function RecurringTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY_TPL);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [materializing, setMaterializing] = useState(false);

  const fetchAll = async () => {
    try {
      const { data } = await api.get("/templates");
      setTemplates(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const startEdit = (tpl) => {
    setEditing(tpl.id);
    setForm({ ...EMPTY_TPL, ...tpl });
  };

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_TPL);
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_TPL);
  };

  const toggleWeekday = (i) => {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(i)
        ? f.weekdays.filter((w) => w !== i)
        : [...f.weekdays, i].sort(),
    }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_path: data.path }));
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.weekdays.length) {
      toast.error("Seleziona almeno un giorno della settimana");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        portions_total: Number(form.portions_total),
        price: Number(form.price),
      };
      if (editing === "new") {
        await api.post("/templates", payload);
        toast.success("Piatto ricorrente creato");
      } else {
        await api.put(`/templates/${editing}`, payload);
        toast.success("Piatto ricorrente aggiornato");
      }
      cancel();
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo piatto ricorrente?")) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Eliminato");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const materializeToday = async () => {
    setMaterializing(true);
    try {
      const { data } = await api.post("/templates/materialize-today");
      if (data.created === 0) {
        toast.info("Nessun piatto da generare (già presenti o nessun template per oggi)");
      } else {
        toast.success(`${data.created} piatto/i pubblicato/i per oggi`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMaterializing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
      <Link to="/preparatore" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> Torna alla dashboard
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-widest font-medium mb-2">
            <Repeat className="w-4 h-4" /> Menu settimanale
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Piatti ricorrenti
          </h1>
          <p className="text-muted-foreground mt-1.5 max-w-2xl">
            Definisci i piatti che si ripetono ogni settimana. Vengono pubblicati automaticamente all'inizio di ogni giorno previsto.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={materializeToday}
            disabled={materializing}
            data-testid="materialize-today-button"
            className="px-4 py-2.5 border border-primary/40 text-primary font-medium rounded-md hover:bg-primary hover:text-white active:scale-[0.98] disabled:opacity-50 transition-all inline-flex items-center gap-2"
          >
            <CalendarCheck className="w-4 h-4" /> {materializing ? "Genero…" : "Materializza oggi"}
          </button>
          <button
            onClick={startNew}
            data-testid="new-template-button"
            className="px-4 py-2.5 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] transition-all inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nuovo
          </button>
        </div>
      </div>

      {/* Inline editor */}
      {editing && (
        <form onSubmit={save} className="bg-card border border-border rounded-lg p-6 mt-6 mb-8 space-y-4" data-testid="template-form">
          <h2 className="font-display text-xl font-medium">
            {editing === "new" ? "Nuovo piatto ricorrente" : "Modifica piatto ricorrente"}
          </h2>

          <div>
            <label className="text-sm font-medium mb-1.5 block">Giorni della settimana <span className="text-accent">*</span></label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((w) => (
                <button
                  key={w.i}
                  type="button"
                  onClick={() => toggleWeekday(w.i)}
                  data-testid={`weekday-${w.i}`}
                  className={`px-3 py-2 rounded-md border text-sm font-medium transition-all ${
                    form.weekdays.includes(w.i)
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-muted-foreground border-border hover:border-foreground/30"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome piatto" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-name-input" className="tpl-input" />
            </Field>
            <Field label="Punto di ritiro">
              <input value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} data-testid="tpl-pickup-input" placeholder="es. Cucina al 2° piano" className="tpl-input" />
            </Field>
          </div>

          <Field label="Descrizione">
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="tpl-input resize-none" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Ingredienti">
              <input value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} className="tpl-input" />
            </Field>
            <Field label="Allergeni">
              <input value={form.allergens} onChange={(e) => setForm({ ...form, allergens: e.target.value })} className="tpl-input" />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Porzioni" required>
              <input type="number" min={1} required value={form.portions_total} onChange={(e) => setForm({ ...form, portions_total: e.target.value })} className="tpl-input" />
            </Field>
            <Field label="Prezzo (€)" required>
              <input type="number" step="0.5" min={0} required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="tpl-input" />
            </Field>
            <Field label="Ora cutoff" required>
              <input type="time" required value={form.cutoff_time} onChange={(e) => setForm({ ...form, cutoff_time: e.target.value })} className="tpl-input" />
            </Field>
          </div>

          <Field label="Cutoff: quando chiudono le prenotazioni?">
            <select
              value={form.cutoff_offset_days}
              onChange={(e) => setForm({ ...form, cutoff_offset_days: Number(e.target.value) })}
              data-testid="tpl-cutoff-offset"
              className="tpl-input"
            >
              <option value={0}>Stesso giorno del piatto</option>
              <option value={-1}>Il giorno prima (D-1)</option>
              <option value={-2}>2 giorni prima (D-2)</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Tipico per chi pubblica il pasto di domani: scegli "Il giorno prima" e imposta l'ora di chiusura.
            </p>
          </Field>

          <Field label="Foto piatto">
            <div className="flex items-start gap-3">
              <label className="flex-1 cursor-pointer">
                <div className="px-4 py-2.5 border border-dashed border-border rounded-md text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center gap-2 justify-center">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Caricamento…" : "Carica immagine"}
                </div>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onUpload} className="hidden" />
              </label>
              {form.photo_path && <img src={fileUrl(form.photo_path)} alt="" className="w-20 h-20 object-cover rounded-md border border-border" />}
            </div>
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="tpl-active-toggle"
            />
            Attivo (genera automaticamente nei giorni selezionati)
          </label>

          <div className="flex gap-3 pt-3 border-t border-border">
            <button type="button" onClick={cancel} className="px-5 py-2 border border-border rounded-md hover:bg-muted">Annulla</button>
            <button type="submit" disabled={saving} data-testid="save-template-button" className="px-5 py-2 bg-primary text-white font-medium rounded-md hover:bg-[#436343] disabled:opacity-50">
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </form>
      )}

      {/* List */}
      <section className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-8 text-center" data-testid="templates-empty">
            <Repeat className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-1">Nessun piatto ricorrente configurato.</p>
            <p className="text-xs text-muted-foreground">Crea il tuo primo template per automatizzare il menu settimanale.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="templates-grid">
            {templates.map((tpl) => (
              <article key={tpl.id} className="bg-card border border-border rounded-lg p-5" data-testid={`template-${tpl.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-display font-medium text-lg">{tpl.name}</div>
                    <div className="text-xs text-muted-foreground">€ {Number(tpl.price).toFixed(2)} · {tpl.portions_total} porzioni · cutoff {tpl.cutoff_time}</div>
                  </div>
                  {!tpl.active && <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">disattivo</span>}
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {WEEKDAYS.map((w) => (
                    <span key={w.i} className={`text-xs px-2 py-1 rounded ${tpl.weekdays.includes(w.i) ? "bg-primary/15 text-primary font-medium" : "bg-muted/50 text-muted-foreground/50"}`}>
                      {w.label}
                    </span>
                  ))}
                </div>
                {tpl.pickup_location && (
                  <div className="text-xs text-muted-foreground mb-3">📍 {tpl.pickup_location}</div>
                )}
                <div className="flex gap-2 pt-3 border-t border-border">
                  <button onClick={() => startEdit(tpl)} data-testid={`edit-template-${tpl.id}`} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <Edit className="w-3.5 h-3.5" /> Modifica
                  </button>
                  <button onClick={() => remove(tpl.id)} data-testid={`delete-template-${tpl.id}`} className="text-sm text-accent hover:underline inline-flex items-center gap-1 ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Elimina
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <style>{`
        .tpl-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          background: white;
          border: 1px solid hsl(var(--border));
          border-radius: 0.375rem;
          font-family: "IBM Plex Sans", sans-serif;
          font-size: 0.9rem;
        }
        .tpl-input:focus {
          outline: none;
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 3px hsla(120, 18%, 41%, 0.15);
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label}{required && <span className="text-accent ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
